import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole =
  | "admin"
  | "moderator"
  | "owner"
  | "user"
  | "vakant_owner"
  | "vacanty_admin"
  | "catalog_editor"
  | "catalog_reviewer"
  | "schemata_proposer"
  | "schemata_approver"
  | "schemata_implementer";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [rolesResolved, setRolesResolved] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setSessionResolved(true);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setSessionResolved(true);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setRoles([]);
      setRolesResolved(sessionResolved);
      return;
    }
    setRolesResolved(false);
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (cancelled) return;
        setRoles((data ?? []).map((r) => r.role as AppRole));
        setRolesResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, sessionResolved]);

  const loading = !sessionResolved || !rolesResolved;

  const hasRole = (r: AppRole) => roles.includes(r);
  const isStaff = hasRole("admin") || hasRole("moderator");

  return { session, user, roles, hasRole, isStaff, loading };
}
